import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { getCurrentUserName, canApproveGrn } from '../../utils/auth';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocalShipping as GrnIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon
} from '@mui/icons-material';
import {
  getGRNList,
  createGRN,
  getGRNSuppliers,
  getProducts,
  updatePendingGRN,
  approveGRN
} from '../../services/api';
import { PRODUCT_CATEGORIES } from '../../constants/productCategories';

const emptyItem = () => ({
  is_new_product: false,
  product_id: null,
  product: null,
  name: '',
  category: '',
  barcode: '',
  price: '',
  quantity_received: 1,
  unit_price: '',
  serial_numbers: '',
  batch_number: '',
  expiry_date: '',
  supplier_warranty_months: '',
  supplier_warranty_end: ''
});

const addMonthsToDate = (months) => {
  const m = parseInt(months, 10);
  if (!m || m < 1) return '';
  const d = new Date();
  d.setMonth(d.getMonth() + m);
  return d.toISOString().split('T')[0];
};

const statusColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'pending_approval') return 'warning';
  if (status === 'cancelled') return 'error';
  return 'default';
};

const GRN = () => {
  const [grnList, setGrnList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editPendingOpen, setEditPendingOpen] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');

  const isAdminApprover = canApproveGrn();

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    supplier_id: '',
    product_id: '',
    serial_number: '',
    status: canApproveGrn() ? 'pending_approval' : ''
  });

  const [formData, setFormData] = useState({
    supplier_id: null,
    supplier_name: '',
    notes: '',
    items: [emptyItem()]
  });

  const cashierName = getCurrentUserName();

  useEffect(() => {
    fetchGRNList();
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchGRNList = async (filterParams = filters) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      Object.entries(filterParams).forEach(([key, val]) => {
        if (val) params.append(key, val);
      });
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await getGRNList(query);
      if (response.success) {
        setGrnList(response.data || []);
      } else {
        setError(response.message || 'Failed to load GRN records');
      }
    } catch (err) {
      setError(err.message || 'Failed to load GRN records');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await getGRNSuppliers();
      if (response.success) setSuppliers(response.data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      if (response.success) setProducts(response.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      supplier_id: null,
      supplier_name: '',
      notes: '',
      items: [emptyItem()]
    });
    setDialogOpen(true);
  };

  const handleItemChange = (index, field, value) => {
    const items = [...formData.items];
    const next = { ...items[index], [field]: value };
    if (field === 'supplier_warranty_months') {
      next.supplier_warranty_end = addMonthsToDate(value) || next.supplier_warranty_end;
    }
    items[index] = next;
    setFormData(prev => ({ ...prev, items }));
  };

  const handleProductSelect = (index, product) => {
    const items = [...formData.items];
    if (product) {
      items[index] = {
        ...items[index],
        is_new_product: false,
        product_id: product.id,
        product,
        name: product.name,
        category: product.category || '',
        barcode: product.barcode || '',
        price: product.price || ''
      };
    } else {
      items[index] = { ...emptyItem() };
    }
    setFormData(prev => ({ ...prev, items }));
  };

  const addItem = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');

      if (!formData.supplier_id && !formData.supplier_name.trim()) {
        setError('Supplier is required');
        return;
      }

      for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        const qty = parseInt(item.quantity_received, 10) || 0;
        if (!item.is_new_product && !item.product_id) {
          setError(`Line ${i + 1}: select a product (or mark as new product)`);
          return;
        }
        if (item.is_new_product && (!item.name || !item.price)) {
          setError(`Line ${i + 1}: new product needs name and selling price`);
          return;
        }
        if (item.is_new_product && !item.category) {
          setError(`Line ${i + 1}: select a category`);
          return;
        }
        if (qty <= 0) {
          setError(`Line ${i + 1}: quantity must be greater than 0`);
          return;
        }
        const serials = String(item.serial_numbers || '')
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (serials.length > 0 && serials.length !== qty) {
          setError(`Line ${i + 1}: enter ${qty} serial number(s) (comma-separated), or change quantity to ${serials.length}`);
          return;
        }
      }

      const payload = {
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        notes: formData.notes,
        items: formData.items.map(item => ({
          is_new_product: item.is_new_product,
          product_id: item.is_new_product ? null : item.product_id,
          name: item.name,
          category: item.category || null,
          barcode: item.barcode,
          price: item.price,
          quantity_received: parseInt(item.quantity_received, 10) || 1,
          unit_price: item.unit_price ? parseFloat(item.unit_price) : null,
          serial_numbers: item.serial_numbers,
          batch_number: item.batch_number || null,
          expiry_date: item.expiry_date || null,
          supplier_warranty_months: item.supplier_warranty_months
            ? parseInt(item.supplier_warranty_months, 10)
            : null,
          supplier_warranty_end: item.supplier_warranty_end || null
        }))
      };

      const response = await createGRN(payload);
      if (response.success) {
        setSuccess(`GRN ${response.data.grn_number} submitted for admin approval`);
        setDialogOpen(false);
        fetchGRNList();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.message || response.error || 'Failed to create GRN');
      }
    } catch (err) {
      setError(err.message || err.error || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleView = (grn) => {
    setSelectedGrn(grn);
    setViewDialogOpen(true);
  };

  const handleOpenEditPending = (grn) => {
    setSelectedGrn(grn);
    setEditNotes(grn.notes || '');
    setEditItems((grn.details || []).map((d) => ({
      grn_detail_id: d.grn_detail_id,
      is_new_product: !!d.is_new_product,
      product_name: d.product_name || d.product?.name || '',
      category: d.category || d.product?.category || '',
      barcode: d.barcode || d.product?.barcode || '',
      selling_price: d.selling_price ?? d.product?.price ?? '',
      unit_price: d.unit_price ?? '',
      quantity_received: d.quantity_received,
      serial_numbers: Array.isArray(d.serial_numbers) ? d.serial_numbers.join(', ') : ''
    })));
    setEditPendingOpen(true);
  };

  const handleSavePending = async () => {
    if (!selectedGrn) return;
    try {
      setSaving(true);
      setError('');
      const response = await updatePendingGRN(selectedGrn.grn_id, {
        notes: editNotes,
        items: editItems
      });
      if (response.success) {
        setSuccess('Pending GRN updated');
        setEditPendingOpen(false);
        fetchGRNList();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to update pending GRN');
      }
    } catch (err) {
      setError(err.message || 'Failed to update pending GRN');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (grn) => {
    if (!window.confirm(`Approve ${grn.grn_number}? Products will be created/updated under their categories.`)) {
      return;
    }
    try {
      setSaving(true);
      setError('');
      const response = await approveGRN(grn.grn_id);
      if (response.success) {
        setSuccess(`GRN ${grn.grn_number} approved`);
        setViewDialogOpen(false);
        setEditPendingOpen(false);
        fetchGRNList();
        fetchProducts();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.message || 'Failed to approve GRN');
      }
    } catch (err) {
      setError(err.message || 'Failed to approve GRN');
    } finally {
      setSaving(false);
    }
  };

  const applyFilters = () => fetchGRNList(filters);

  const clearFilters = () => {
    const cleared = {
      start_date: '',
      end_date: '',
      supplier_id: '',
      product_id: '',
      serial_number: '',
      status: ''
    };
    setFilters(cleared);
    fetchGRNList(cleared);
  };

  const categoryField = (value, onChange, required = false) => (
    <FormControl fullWidth required={required}>
      <InputLabel>Category{required ? ' *' : ''}</InputLabel>
      <Select
        label={`Category${required ? ' *' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {PRODUCT_CATEGORIES.map((c) => (
          <MenuItem key={c} value={c}>{c}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <GrnIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Goods Receipt (GRN)</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchGRNList()} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            New GRN
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {isAdminApprover && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only Admin / Super Admin can approve GRNs. Pending requests appear below — edit if needed, then Approve.
          {filters.status === 'pending_approval' ? ` Showing ${grnList.length} pending.` : ''}
        </Alert>
      )}
      {!isAdminApprover && (
        <Alert severity="info" sx={{ mb: 2 }}>
          New GRNs require Admin approval. You can submit requests; Admin will approve before products go live.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="Start Date" type="date"
              value={filters.start_date} onChange={(e) => setFilters(p => ({ ...p, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="End Date" type="date"
              value={filters.end_date} onChange={(e) => setFilters(p => ({ ...p, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status || ''}
                onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending_approval">Pending Approval</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete size="small" options={suppliers}
              getOptionLabel={(o) => o.name || ''}
              value={suppliers.find(s => s.id === filters.supplier_id) || null}
              onChange={(_, val) => setFilters(p => ({ ...p, supplier_id: val?.id || '' }))}
              renderInput={(params) => <TextField {...params} label="Supplier" />} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete size="small" options={products}
              getOptionLabel={(o) => `${o.name} (${o.product_code})`}
              value={products.find(p => p.id === filters.product_id) || null}
              onChange={(_, val) => setFilters(p => ({ ...p, product_id: val?.id || '' }))}
              renderInput={(params) => <TextField {...params} label="Product" />} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" label="Serial Number"
              value={filters.serial_number}
              onChange={(e) => setFilters(p => ({ ...p, serial_number: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              helperText="Press Enter or Apply" />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="contained" onClick={applyFilters} sx={{ mr: 1 }}>Apply</Button>
            <Button fullWidth variant="outlined" onClick={clearFilters} sx={{ mt: 1 }}>Clear</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>GRN #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Received By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grnList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No GRN records found</TableCell>
                  </TableRow>
                ) : grnList.map(grn => (
                  <TableRow key={grn.grn_id} hover>
                    <TableCell><strong>{grn.grn_number}</strong></TableCell>
                    <TableCell>{grn.received_date}</TableCell>
                    <TableCell>{grn.received_time}</TableCell>
                    <TableCell>{grn.supplier?.name || '—'}</TableCell>
                    <TableCell>{grn.total_items}</TableCell>
                    <TableCell>{grn.cashier_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={grn.status === 'pending_approval' ? 'Pending Approval' : grn.status}
                        size="small"
                        color={statusColor(grn.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleView(grn)} title="View">
                        <ViewIcon />
                      </IconButton>
                      {isAdminApprover && grn.status === 'pending_approval' && (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleOpenEditPending(grn)} title="Edit before approve">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="success" onClick={() => handleApprove(grn)} title="Approve" disabled={saving}>
                            <ApproveIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create GRN Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Goods Receipt Note</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete freeSolo options={suppliers.map(s => s.name)}
                value={formData.supplier_name}
                onInputChange={(_, val) => setFormData(p => ({ ...p, supplier_name: val, supplier_id: null }))}
                onChange={(_, val) => {
                  const supplier = suppliers.find(s => s.name === val);
                  setFormData(p => ({
                    ...p,
                    supplier_name: typeof val === 'string' ? val : val || '',
                    supplier_id: supplier?.id || null
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Supplier *" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Received By" value={cashierName} disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2}
                value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Items</Typography>

          {formData.items.map((item, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeItem(index)}
                  disabled={formData.items.length <= 1}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={item.is_new_product}
                      onChange={(e) => handleItemChange(index, 'is_new_product', e.target.checked)} />}
                    label="New Product" />
                </Grid>
                {!item.is_new_product ? (
                  <>
                    <Grid item xs={12} sm={8}>
                      <Autocomplete options={products}
                        getOptionLabel={(o) => `${o.name} (${o.product_code})`}
                        value={item.product}
                        onChange={(_, val) => handleProductSelect(index, val)}
                        renderInput={(params) => <TextField {...params} label="Select Product *" />} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {categoryField(item.category, (v) => handleItemChange(index, 'category', v))}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Product Name *" value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {categoryField(item.category, (v) => handleItemChange(index, 'category', v), true)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Barcode" value={item.barcode}
                        onChange={(e) => handleItemChange(index, 'barcode', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Selling Price *" type="number" value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value)} />
                    </Grid>
                  </>
                )}
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Quantity *" type="number" value={item.quantity_received}
                    onChange={(e) => handleItemChange(index, 'quantity_received', e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Unit Price" type="number" value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Serial Numbers" value={item.serial_numbers}
                    onChange={(e) => handleItemChange(index, 'serial_numbers', e.target.value)}
                    placeholder="Leave empty if this batch has no serials"
                    helperText="Optional. Enter only real serials (comma-separated). If empty, stock is added without serial numbers — nothing is auto-generated." />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Batch Number" value={item.batch_number}
                    onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Expiry Date" type="date" value={item.expiry_date}
                    onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Shelf life / batch expiry" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Supplier Warranty (months)" type="number"
                    value={item.supplier_warranty_months}
                    onChange={(e) => handleItemChange(index, 'supplier_warranty_months', e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="e.g. 3 for 3 months" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Supplier Warranty End" type="date"
                    value={item.supplier_warranty_end}
                    onChange={(e) => handleItemChange(index, 'supplier_warranty_end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Auto-filled from months" />
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={addItem}>Add Item</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Request admin approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View GRN Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>GRN Details — {selectedGrn?.grn_number}</DialogTitle>
        <DialogContent>
          {selectedGrn && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Date</Typography>
                  <Typography>{selectedGrn.received_date} {selectedGrn.received_time}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Supplier</Typography>
                  <Typography>{selectedGrn.supplier?.name || '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Received By</Typography>
                  <Typography>{selectedGrn.cashier_name}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip label={selectedGrn.status} size="small" color={statusColor(selectedGrn.status)} /></Grid>
              </Grid>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell>Serial Numbers</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell>Supplier Warranty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedGrn.details || []).map(d => (
                      <TableRow key={d.grn_detail_id}>
                        <TableCell>{d.product?.name || d.product_name || d.product_id || '—'}</TableCell>
                        <TableCell>{d.category || d.product?.category || '—'}</TableCell>
                        <TableCell align="right">{d.quantity_received}</TableCell>
                        <TableCell align="right">{d.unit_price || '—'}</TableCell>
                        <TableCell>
                          {(Array.isArray(d.serial_numbers) ? d.serial_numbers : []).join(', ') || '—'}
                        </TableCell>
                        <TableCell>{d.batch_number || '—'}</TableCell>
                        <TableCell>
                          {d.supplier_warranty_months
                            ? `${d.supplier_warranty_months} mo`
                            : ''}
                          {d.supplier_warranty_end
                            ? `${d.supplier_warranty_months ? ' / ' : ''}until ${d.supplier_warranty_end}`
                            : (d.supplier_warranty_months ? '' : '—')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isAdminApprover && selectedGrn?.status === 'pending_approval' && (
            <>
              <Button startIcon={<EditIcon />} onClick={() => { setViewDialogOpen(false); handleOpenEditPending(selectedGrn); }}>
                Edit
              </Button>
              <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => handleApprove(selectedGrn)} disabled={saving}>
                Approve
              </Button>
            </>
          )}
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Admin edit pending */}
      <Dialog open={editPendingOpen} onClose={() => setEditPendingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit pending GRN — {selectedGrn?.grn_number}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Notes" multiline rows={2} sx={{ mt: 1, mb: 2 }}
            value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
          {editItems.map((item, index) => (
            <Paper key={item.grn_detail_id || index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Product Name" value={item.product_name}
                    onChange={(e) => {
                      const next = [...editItems];
                      next[index] = { ...next[index], product_name: e.target.value };
                      setEditItems(next);
                    }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {categoryField(item.category, (v) => {
                    const next = [...editItems];
                    next[index] = { ...next[index], category: v };
                    setEditItems(next);
                  }, !!item.is_new_product)}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Selling Price" type="number" value={item.selling_price}
                    onChange={(e) => {
                      const next = [...editItems];
                      next[index] = { ...next[index], selling_price: e.target.value };
                      setEditItems(next);
                    }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Unit Price" type="number" value={item.unit_price}
                    onChange={(e) => {
                      const next = [...editItems];
                      next[index] = { ...next[index], unit_price: e.target.value };
                      setEditItems(next);
                    }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Qty" type="number" value={item.quantity_received}
                    onChange={(e) => {
                      const next = [...editItems];
                      next[index] = { ...next[index], quantity_received: e.target.value };
                      setEditItems(next);
                    }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Barcode" value={item.barcode}
                    onChange={(e) => {
                      const next = [...editItems];
                      next[index] = { ...next[index], barcode: e.target.value };
                      setEditItems(next);
                    }} />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPendingOpen(false)}>Cancel</Button>
          <Button variant="outlined" onClick={handleSavePending} disabled={saving}>Save edits</Button>
          <Button variant="contained" color="success" onClick={() => handleApprove(selectedGrn)} disabled={saving}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GRN;
